/**
 * Shared axios instance for talking to the FastAPI backend.
 *
 * - Base URL comes from `VITE_API_URL` (defaults to /api/v1 via the dev proxy).
 * - Request interceptor injects the Supabase JWT as a Bearer token.
 * - Response interceptor normalizes errors into `{ code, message, status, details }`
 *   so callers always handle errors with the same shape.
 *
 * IMPORTANT: do not import axios anywhere else. Feature `api.ts` files
 * import THIS instance and call `.get/.post/...` on it.
 */

import axios, { AxiosError, type AxiosInstance } from "axios";

import { env } from "./env";
import { supabase } from "./supabase";

export type ApiError = {
  code: string;
  message: string;
  status: number;
  details?: Record<string, unknown>;
};

const api: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 15_000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ code?: string; message?: string; details?: Record<string, unknown> }>) => {
    const status = error.response?.status ?? 0;
    const data = error.response?.data;
    const normalized: ApiError = {
      code: data?.code ?? (status === 0 ? "network_error" : "unknown_error"),
      message: data?.message ?? error.message ?? "Unknown error",
      status,
      details: data?.details,
    };
    return Promise.reject(normalized);
  },
);

export default api;
